import React, { useEffect, useState } from 'react';
import { Title, useDataProvider } from 'react-admin';
import { Box, Button, Paper, Tab, Tabs } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarketOverviewChart, { MarketOverviewItem } from './MarketOverviewChart';
import InventoryDataGrid from './InventoryDataGrid';
import EventLevelView from './EventLevelView';
import ScanLevelView from './ScanLevelView';
import CityStateCheckView from './CityStateCheckView';
import { loadSourceType } from '../../utils/common';

/** A market-type option loaded from the server. */
interface Choice {
    id: string;
    name: string;
}

/** Filter state preserved across market-overview chart re-renders. */
interface OverviewFilters {
    hours: number;
    selectedMarketType: string;
    dropPercentage: string;
}

/**
 * Inventory Drop Analysis page.
 *
 * Renders three tabs:
 * - **Venue-Level** — market overview chart + per-venue data grid (existing behaviour).
 * - **Event-Level** — individual event data grid backed by `EventLevel` OData endpoint.
 * - **Scan-Level** — per-market new-event counts (Latest Day / 7d / 14d / 30d) from
 *   `smallspider_event`, backed by the `ScanLevel` OData endpoint.
 * - **City/State Check** — orphaned `smallspider_event` rows (`VenueId = 0`) grouped by
 *   market type, city, and state, cross-referenced against `venue_city_states`, backed
 *   by the `CityStateCheck` OData endpoint.
 *
 * Overview chart state is lifted here so it is preserved when the user drills
 * down into the venue detail view and then navigates back.
 *
 * **Tab keep-alive strategy:** each tab's content is lazy-mounted on first activation
 * and then kept in the DOM (hidden via `display: none`) rather than unmounted.
 * This preserves filter values, pagination state, and loaded data across tab switches
 * so users do not lose their place when toggling between tabs.
 */
const InventoryDropAnalysis: React.FC = () => {
    const dataProvider = useDataProvider();
    const [tab, setTab] = useState<number>(0); // 0 = Venue-Level, 1 = Event-Level, 2 = Scan-Level, 3 = City/State Check
    const [view, setView] = useState<'overview' | 'detail'>('overview');
    const [selectedMarket, setSelectedMarket] = useState<string>('');
    const [marketTypeChoices, setMarketTypeChoices] = useState<Choice[]>([]);

    /**
     * Tracks which tabs have been activated at least once.
     * Tabs not yet visited are not mounted, avoiding unnecessary API calls on page load.
     * Once activated, a tab remains mounted so its state is preserved across switches.
     */
    const [activeTabs, setActiveTabs] = useState<Set<number>>(new Set([0]));

    // Lift overview state here so it survives drilldown navigation
    const [dataOverview, setDataOverview] = useState<MarketOverviewItem[]>([]);
    const [overviewFilters, setOverviewFilters] = useState<OverviewFilters>({
        hours: 24,
        selectedMarketType: '',
        dropPercentage: '2',
    });

    useEffect(() => {
        loadSourceType(dataProvider, (response: Choice[]) => {
            setMarketTypeChoices(response);
        }, false);
    }, []);

    const handleMarketSelect = (marketType: string) => {
        setSelectedMarket(marketType);
        setView('detail');
    };

    const handleBackToOverview = () => {
        setView('overview');
        setSelectedMarket('');
        // Data is preserved in state — no reload needed
    };

    return (
        <>
            <Title title="Inventory Drop Analysis" />
            <Box sx={{ p: 2 }}>
                <Tabs
                    value={tab}
                    onChange={(_, val: number) => {
                        setTab(val);
                        setView('overview');
                        // Mark tab as activated so its content is mounted and kept alive
                        setActiveTabs(prev => {
                            if (prev.has(val)) return prev;
                            const next = new Set(prev);
                            next.add(val);
                            return next;
                        });
                    }}
                    sx={{ mb: 2 }}
                >
                    <Tab label="Venue-Level" />
                    <Tab label="Event-Level" />
                    <Tab label="Scan-Level" />
                    <Tab label="City/State Check" />
                </Tabs>

                {/* Venue-Level tab — always mounted; hidden via display when inactive */}
                <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
                    {view === 'detail' && (
                        <Box sx={{ mb: 2 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToOverview}
                                variant="outlined"
                                size="small"
                            >
                                Back to Overview
                            </Button>
                        </Box>
                    )}

                    {view === 'overview' && (
                        <Paper sx={{ p: 2 }}>
                            <MarketOverviewChart
                                onMarketSelect={handleMarketSelect}
                                marketTypeChoices={marketTypeChoices}
                                dataOverview={dataOverview}
                                setDataOverview={setDataOverview}
                                overviewFilters={overviewFilters}
                                setOverviewFilters={setOverviewFilters}
                            />
                        </Paper>
                    )}

                    {view === 'detail' && (
                        <InventoryDataGrid
                            key={selectedMarket}
                            initialMarketType={selectedMarket}
                            marketTypeChoices={marketTypeChoices}
                        />
                    )}
                </Box>

                {/* Event-Level tab — lazy-mounted on first activation, then kept alive */}
                <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
                    {activeTabs.has(1) && (
                        <EventLevelView marketTypeChoices={marketTypeChoices} />
                    )}
                </Box>

                {/* Scan-Level tab — lazy-mounted on first activation, then kept alive */}
                <Box sx={{ display: tab === 2 ? 'block' : 'none' }}>
                    {activeTabs.has(2) && (
                        <ScanLevelView />
                    )}
                </Box>

                {/* City/State Check tab — lazy-mounted on first activation, then kept alive */}
                <Box sx={{ display: tab === 3 ? 'block' : 'none' }}>
                    {activeTabs.has(3) && (
                        <CityStateCheckView marketTypeChoices={marketTypeChoices} />
                    )}
                </Box>
            </Box>
        </>
    );
};

const InventoryDropAnalysisPage = {
    name: 'InventoryDropAnalysis',
    list: InventoryDropAnalysis,
};

export default InventoryDropAnalysisPage;
